import classNames from 'classnames';
import { memo, useId, useMemo } from 'react';
import type { PrintTemplateSpec } from 'shared-types';
import type { PrintImageStore } from '../export/printImages';
import { renderTemplatePreviewSvg, type TemplatePreviewOptions } from './templatePreview';
import type { PreviewEnv } from './usePreviewEnv';
import { usePrintLengthUnit } from '../usePrintLengthUnit';

interface TemplateThumbnailProps {
  env: PreviewEnv | null;
  spec: PrintTemplateSpec;
  /** Defaults to the first page. */
  pageId?: string;
  images?: PrintImageStore | null;
  sources?: TemplatePreviewOptions['sources'];
  className?: string;
}

/** A template page with sample content, as an SVG scaled to its box (the paper keeps its aspect). */
const TemplateThumbnail_: React.FC<TemplateThumbnailProps> = ({ env, spec, pageId, images, sources, className }) => {
  const idPrefix = `tt${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const units = usePrintLengthUnit();
  const svg = useMemo(() => {
    const page = pageId ?? spec.pages[0]?.id;
    if (!env || !page) return null;
    return renderTemplatePreviewSvg(env, spec, page, {
      sources: { units, ...sources },
      image: images ? (source) => images.resolve(source) : undefined,
      imageHref: images ? (key) => images.href(key) : undefined,
      idPrefix,
      missing: 'marker',
    });
  }, [env, spec, pageId, images, sources, idPrefix, units]);
  return (
    <div
      className={classNames('flex items-center justify-center [&>svg]:h-auto [&>svg]:max-h-full [&>svg]:w-auto [&>svg]:max-w-full [&>svg]:shadow-md', className)}
      // The markup is ours: produced by the print engine's SVG backend (all text escaped).
      dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
    />
  );
};

export const TemplateThumbnail = memo(TemplateThumbnail_);
